import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import { getAccreditationExplainer } from '@components/coach-accreditation-explainer-dialog'
import useStyles from './coach-membership-banner.styles'

/**
 * Classification ids that get a bio-card membership banner. Matches
 * `getCoachMembershipDisplayAccreditation` — ADM / PGA HOPE are
 * accreditations-band only.
 */
const MEMBERSHIP_BANNER_IDS = new Set([
  'pga-member',
  'pga-associate',
  'pga-student',
  'master-professional',
])

/**
 * Navy / white circular membership crests (not the gold-ring
 * accreditation PNGs). Only full Members and Master Professionals get
 * a crest in the bio banner — `/images/pga-member-logo.svg` includes
 * the "MEMBER" banner and must not appear for Associates, Students,
 * or Affiliates.
 */
const MEMBERSHIP_BANNER_LOGO_SRC = {
  'pga-member': '/images/pga-member-logo.svg',
  'master-professional': '/images/pga-master-logo.svg',
}

/**
 * Two-sentence classification copy. Member wording is the product
 * teaser; Associate / Student / Master Professional stay parallel
 * and classification-accurate. Do not invent tenure years.
 */
const MEMBERSHIP_BANNER_COPY = {
  'pga-member': (name) => (
    `${name} is a PGA of America Member, holding golf's highest credential for professional instruction and leadership. This certified expertise ensures you receive top-tier, trusted coaching tailored to your game.`
  ),
  'pga-associate': (name) => (
    `${name} is a PGA of America Associate, a working golf professional on the PGA's path to full Membership. This training means you work with a coach who is actively building the same skills every Member must earn.`
  ),
  'pga-student': (name) => (
    `${name} is a PGA of America Student, enrolled in a PGA Golf Management university program. This training means you get coaching from someone learning under the same education that produces every PGA Member.`
  ),
  'master-professional': (name) => (
    `${name} is a PGA of America Master Professional, holding the PGA's highest education-and-experience designation for professional instruction and leadership. This certified expertise ensures you receive top-tier, trusted coaching tailored to your game.`
  ),
}

function resolveBannerLogoSrc(accreditation) {
  return MEMBERSHIP_BANNER_LOGO_SRC[accreditation?.id] || null
}

/**
 * Personalized 2-sentence banner copy naming this coach and their
 * classification. Tenure years are never included — do not invent
 * them when the API has not supplied a count.
 *
 * @param {{
 *   coachName?: string | null,
 *   accreditationId?: string | null,
 * }} input
 * @returns {string | null}
 */
function getCoachMembershipBannerCopy({
  coachName,
  accreditationId,
} = {}) {
  const name = typeof coachName === 'string' ? coachName.trim() : ''
  const buildCopy = MEMBERSHIP_BANNER_COPY[accreditationId]
  if (!name || !buildCopy) return null
  return buildCopy(name)
}

function countSentences(text) {
  return text
    .trim()
    .split(/(?<=[.!?])(?:\s+|$)/)
    .filter((part) => part.trim().length > 0)
    .length
}

/**
 * Compact membership banner above the bio on the flagged profile
 * (under the tagline, before Tools & Technology / Accreditations
 * lists when those render). Hidden when the coach has no Member /
 * Associate / Student / Master Professional classification — never
 * an empty bar. Read More sits inline after the second sentence and
 * opens the shared `CoachAccreditationExplainerDialog` (same modal
 * as MembershipTag).
 */
function CoachMembershipBanner({
  accreditation,
  coachName,
  onReadMore,
}) {
  const styles = useStyles()
  const explainer = getAccreditationExplainer(accreditation?.id)
  const copy = getCoachMembershipBannerCopy({
    coachName,
    accreditationId: accreditation?.id,
  })
  const logoSrc = resolveBannerLogoSrc(accreditation)

  if (
    !accreditation?.id
    || !MEMBERSHIP_BANNER_IDS.has(accreditation.id)
    || !copy
  ) {
    return null
  }

  const logoAlt = accreditation.alt || explainer?.title || copy
  const readMoreLabel = explainer
    ? `Read more about ${explainer.title}`
    : 'Read more about this PGA classification'

  return (
    <Box
      className={styles.root}
      data-testid="coach-membership-banner"
      aria-label={explainer?.title || copy}
    >
      {logoSrc ? (
        <Box className={styles.logoWrapper}>
          <img
            src={composeOptimizedImageUrl(logoSrc, 96)}
            alt={logoAlt}
            className={styles.logo}
          />
        </Box>
      ) : null}
      <Box className={styles.copy}>
        <Typography variant="body2" className={styles.teaser} component="p">
          {copy}
          {explainer ? (
            <>
              {' '}
              <button
                type="button"
                className={styles.readMore}
                aria-haspopup="dialog"
                aria-label={readMoreLabel}
                onClick={() => onReadMore(accreditation.id)}
              >
                Read More
              </button>
            </>
          ) : null}
        </Typography>
      </Box>
    </Box>
  )
}

CoachMembershipBanner.propTypes = {
  accreditation: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    logoSrc: PropTypes.string,
    alt: PropTypes.string,
  }),
  coachName: PropTypes.string,
  onReadMore: PropTypes.func.isRequired,
}

CoachMembershipBanner.defaultProps = {
  accreditation: null,
  coachName: null,
}

export {
  MEMBERSHIP_BANNER_IDS,
  MEMBERSHIP_BANNER_LOGO_SRC,
  getCoachMembershipBannerCopy,
  resolveBannerLogoSrc,
  countSentences,
}
export default CoachMembershipBanner
